import PermIdentityOutlined from '@mui/icons-material/PermIdentityOutlined'
import React from 'react'
import { Button } from 'sutra/components/Button'
import { Chiclet } from 'sutra/components/Chiclet/Chiclet'
import { Toggle } from 'sutra/components/Toggle/Toggle'

import {
  AgentRowKebab,
  EditIcon,
  ShareIcon,
} from 'features/trend-detection/features/agent-list/components/AgentRowKebab'
import { StatusPill } from 'features/trend-detection/features/agent-list/components/StatusPill'
import {
  Actions,
  Header,
  Left,
  MetaItem,
  MetaRow,
  MetaText,
  PageTitle,
  TitleRow,
} from 'features/trend-detection/features/issue-agent-detail/components/DetailHeader/styles'
import type {
  BenchmarkingAgentDetail,
  IssueAgentResponse,
} from 'features/trend-detection/types'

export interface DetailHeaderHandlers {
  onToggleActive: () => void
  onShare: () => void
  onEdit: () => void
  onDelete: () => void
}

export type DetailHeaderAgent = IssueAgentResponse | BenchmarkingAgentDetail

export interface DetailHeaderProps {
  agent: DetailHeaderAgent
  handlers: DetailHeaderHandlers
}

export const DetailHeader: React.FC<DetailHeaderProps> = ({
  agent,
  handlers,
}) => {
  // Read `is_enabled` (the pause/resume state `setAgentEnabled` writes),
  // not the derived `is_active` — see AgentListView.
  const { name, is_enabled, is_default, is_editor, created_by } = agent
  // Both issue and benchmarking agents populate `stale_filters` — the wire
  // shape matches (`StaleFilterIssue[]`), so one guard covers both.
  const staleFilters =
    'stale_filters' in agent ? (agent.stale_filters ?? []) : []
  const staleCount = staleFilters.length
  // Mirrors `getCreatedByLabel` in AgentListView, plus a "By " prefix.
  const ownerLabel = is_default
    ? 'By Yogi'
    : created_by?.name
      ? `By ${created_by.name}`
      : created_by?.email
        ? `By ${created_by.email}`
        : 'By —'

  return (
    <Header>
      <Left>
        <TitleRow>
          <PageTitle>{name}</PageTitle>
          {is_default && <Chiclet $tone="gray">Default</Chiclet>}
        </TitleRow>
        <MetaRow>
          <Toggle
            checked={is_enabled}
            onChange={handlers.onToggleActive}
            aria-label={`Toggle ${name}`}
          />
          <StatusPill status={is_enabled ? 'active' : 'paused'} />
          {staleCount > 0 && (
            <Chiclet $tone="warning">Filters need attention</Chiclet>
          )}
          {ownerLabel && (
            <MetaItem>
              <PermIdentityOutlined sx={{ fontSize: 16 }} />
              <MetaText forwardedAs="span">{ownerLabel}</MetaText>
            </MetaItem>
          )}
        </MetaRow>
      </Left>
      <Actions>
        {!is_default && (
          <Button variant="secondary" size="sm" onClick={handlers.onShare}>
            <ShareIcon />
            Share
          </Button>
        )}
        {!is_default && is_editor && (
          <Button variant="secondary" size="sm" onClick={handlers.onEdit}>
            <EditIcon />
            Edit Agent
          </Button>
        )}
        {!is_default && (
          <AgentRowKebab
            agentName={name}
            isEditor={is_editor}
            isActive={is_enabled}
            isDefault={is_default}
            // Share/Edit/"View details" are surfaced as own affordances.
            hideItems={
              is_editor ? ['view', 'edit', 'share'] : ['view', 'share']
            }
            dropdownOffsetY={12}
            handlers={{
              onViewDetails: () => {},
              onEdit: handlers.onEdit,
              onToggleActive: handlers.onToggleActive,
              onShare: handlers.onShare,
              onDelete: handlers.onDelete,
            }}
          />
        )}
      </Actions>
    </Header>
  )
}
